import React, { useMemo, useState } from 'react';
import axios from 'axios';

import { openTopLevelDialog } from '~/components/CmsMain/globals';
import type { ClaimClosingReasonValues } from '~/components/exposures/ClaimClosingReasonFormik';
import CloseClaimDialog from '~/components/exposures/CloseClaimDialog';
import { ClosingReasonDialog } from '~/components/exposures/CloseExposureDialog';
import { useExposureClosingDisabledReason } from '~/components/exposures/ExposureUtils';
import { useCms } from '~/components/hooks/useCms';
import useOrganization from '~/components/OrganizationContext';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, isLegacyOrgFeatureEnabled, reportAxiosError } from '~/Utils';
import { getClaimExposuresWithGeneralFakeExposure } from '~/Utils/ClaimUtils';
import type {
  ClaimForClose,
  CloseClaimReasonConfig,
  CloseClaimType,
  OrgAdditionalConfigs,
  OrgRequiredFieldsForCloseClaim,
} from '~/Utils/closeClaimUtils';
import { claimCloseTypes, getClaimCloseDisabledReasons, validateClaimBeforeClose } from '~/Utils/closeClaimUtils';

interface ClaimClosePayload {
  close_note?: string;
  reason?: string;
  extra?: string;
  claim_reason?: string;
  claim_extra?: string;
  should_dismiss_all_claim_notifications?: boolean;
}

interface ClaimCloseFlowOptions {
  claim: ClaimForClose;
  /** Runs after `POST /claims/{id}/close` succeeds, with the close note that was posted. */
  onClosed: (closeNote?: string) => void | Promise<void>;
  /** Title of the configurable closing-reason dialog. */
  configurableCloseTitle?: string;
}

interface ClaimCloseFlow {
  closeTypes: CloseClaimType[];
  closeDisabledReasons: string[];
  isConfigurableClosingReasonEnabled: boolean;
  isClosing: boolean;
  /** True while one of `closeDialogs` is on screen. */
  isCloseDialogOpen: boolean;
  /** Validates, collects an exposure closing reason if the org needs one, then posts the close. */
  startClose: (closeType: CloseClaimType) => Promise<void>;
  /** Posts the close with a configurable closing reason, however the surface collected it. */
  submitConfigurableClose: (values: ClaimClosingReasonValues) => Promise<void>;
  openConfigurableCloseDialog: () => void;
  closeDialogs: React.ReactNode;
}

/**
 * The whole claim-close flow every surface offering a claim close runs: the org's close policy
 * (which `POST /claims/{id}/close` does not re-check), the close types the org exposes, the
 * pre-close validation, the closing-reason dialogs and the close request itself. A surface
 * only supplies the trigger UI and what happens after a successful close.
 */
export const useClaimCloseFlow = ({
  claim,
  onClosed,
  configurableCloseTitle = 'Close Claim',
}: ClaimCloseFlowOptions): ClaimCloseFlow => {
  const { userOrganization, user } = useCms();
  const { closeClaimReasons, orgAdditionalConfigs, orgRequiredFieldsForCloseClaim } = useOrganization() as {
    closeClaimReasons?: Record<string, CloseClaimReasonConfig>;
    orgAdditionalConfigs?: OrgAdditionalConfigs;
    orgRequiredFieldsForCloseClaim?: OrgRequiredFieldsForCloseClaim;
  };
  const [isClosing, setIsClosing] = useState(false);
  const [pendingCloseNote, setPendingCloseNote] = useState<string | undefined>(undefined);
  const [isClosingReasonDialogOpen, setIsClosingReasonDialogOpen] = useState(false);
  const [isConfigurableCloseDialogOpen, setIsConfigurableCloseDialogOpen] = useState(false);

  const isConfigurableClosingReasonEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.NEW_CLOSING_REASON
  );
  const isExposureFinancialOnlyOrg = isLegacyOrgFeatureEnabled(
    userOrganization,
    LEGACY_ORG_FEATURES.HOSPITALITY_BOOST_CLAIM_LEVEL_COVERAGE_DECISION
  );
  const isTotalSettlementAmountEnabled = isLegacyOrgFeatureEnabled(
    userOrganization,
    LEGACY_ORG_FEATURES.MGM_GL_TOTAL_SETTLEMENT_AMOUNT
  );
  const exposureClosingDisabledReason = useExposureClosingDisabledReason(
    getClaimExposuresWithGeneralFakeExposure(claim)
  );

  const closeTypes = useMemo(
    () => claimCloseTypes(claim.type, orgAdditionalConfigs, user, closeClaimReasons),
    [claim.type, orgAdditionalConfigs, user, closeClaimReasons]
  );

  const closeDisabledReasons = useMemo(
    () =>
      getClaimCloseDisabledReasons({
        claim,
        isExposureFinancialOnlyOrg,
        exposureClosingDisabledReason,
        orgRequiredFieldsForCloseClaim,
        isConfigurableClosingReasonEnabled,
      }),
    [
      claim,
      isExposureFinancialOnlyOrg,
      exposureClosingDisabledReason,
      orgRequiredFieldsForCloseClaim,
      isConfigurableClosingReasonEnabled,
    ]
  );

  const submitClose = async (payload: ClaimClosePayload): Promise<void> => {
    try {
      setIsClosing(true);
      await axios.post(`/api/v1/claims/${claim.id}/close`, payload);
      await onClosed(payload.close_note);
      setIsClosingReasonDialogOpen(false);
      setIsConfigurableCloseDialogOpen(false);
      setPendingCloseNote(undefined);
      setIsClosing(false);
    } catch (error) {
      reportAxiosError(error);
      setIsClosing(false);
    }
  };

  const submitConfigurableClose = (values: ClaimClosingReasonValues): Promise<void> =>
    submitClose({
      close_note: '',
      reason: '',
      extra: '',
      claim_reason: values.claim_reason,
      claim_extra: values.claim_extra,
      should_dismiss_all_claim_notifications: values.should_dismiss_all_claim_notifications,
    });

  const startClose = async (closeType: CloseClaimType): Promise<void> => {
    const { isValid, message } = validateClaimBeforeClose({
      claim,
      closeTypeKey: closeType.key,
      isTotalSettlementAmountEnabled,
    });
    if (!isValid) {
      openTopLevelDialog({ title: "Can't close claim", message });
      return;
    }

    // Financial-only orgs close the claim's open exposures along with it, and the exposure close
    // rejects a missing reason, so the reason has to be collected before posting.
    if (isExposureFinancialOnlyOrg) {
      setPendingCloseNote(closeType.note);
      setIsClosingReasonDialogOpen(true);
      return;
    }

    await submitClose({ close_note: closeType.note });
  };

  const closeDialogs = (
    <>
      {isClosingReasonDialogOpen && ( // DEPRECATED - only for organizations where exposures are financial only
        <ClosingReasonDialog
          title="Closing Reason"
          claim={claim}
          exposures={claim.exposures}
          onClose={() => {
            setIsClosingReasonDialogOpen(false);
            setPendingCloseNote(undefined);
          }}
          onReasonChosen={(values: { reason: string; extra: string }) =>
            submitClose({ close_note: pendingCloseNote, reason: values.reason, extra: values.extra })
          }
        />
      )}
      {isConfigurableCloseDialogOpen && (
        <CloseClaimDialog
          title={configurableCloseTitle}
          claim={claim}
          onClose={() => setIsConfigurableCloseDialogOpen(false)}
          onReasonChosen={submitConfigurableClose}
        />
      )}
    </>
  );

  return {
    closeTypes,
    closeDisabledReasons,
    isConfigurableClosingReasonEnabled,
    isClosing,
    isCloseDialogOpen: isClosingReasonDialogOpen || isConfigurableCloseDialogOpen,
    startClose,
    submitConfigurableClose,
    openConfigurableCloseDialog: () => setIsConfigurableCloseDialogOpen(true),
    closeDialogs,
  };
};
